import { useMemo } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isToday } from 'date-fns';
import type { CalendarEvent, Member } from '../types/calendar';
import { useSwipe } from '../hooks/useSwipe';

interface MonthMobileViewProps {
  currentDate: Date;
  members: Member[];
  eventsByDate: Record<string, Record<string, CalendarEvent[]>>;
  onDayClick: (date: Date) => void;
  onPrevMonth?: () => void;
  onNextMonth?: () => void;
  weekStartsOn?: 0 | 1;
}

export function MonthMobileView({ currentDate, members, eventsByDate, onDayClick, onPrevMonth, onNextMonth, weekStartsOn = 0 }: MonthMobileViewProps) {
  const days = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn });
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate, weekStartsOn]);

  const weekDayLabels = useMemo(() => {
    const start = startOfWeek(new Date(), { weekStartsOn });
    return eachDayOfInterval({ start, end: new Date(start.getTime() + 6 * 86400000) })
      .map(d => format(d, 'EEE').charAt(0));
  }, [weekStartsOn]);

  // Add swipe gesture support for month navigation
  const swipeRef = useSwipe({
    onSwipeLeft: onNextMonth, // Swipe left → next month
    onSwipeRight: onPrevMonth, // Swipe right → previous month
  });

  return (
    <div ref={swipeRef} className="flex flex-col h-full bg-bg-primary p-3">
      {/* Day of week headers */}
      <div className="grid grid-cols-7 mb-2">
        {weekDayLabels.map((label, i) => (
          <div key={i} className="text-center text-xs font-semibold text-text-secondary py-1">
            {label}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 flex-1">
        {days.map((day) => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dayEvents = eventsByDate[dateKey] || {};
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isCurrentDay = isToday(day);

          // Get unique member colors who have events this day
          const memberColors: string[] = [];
          for (const member of members) {
            if ((dayEvents[member.id]?.length ?? 0) > 0 && memberColors.length < 4) {
              memberColors.push(member.color);
            }
          }

          return (
            <button
              key={dateKey}
              onClick={() => onDayClick(day)}
              className={`flex flex-col items-center justify-center py-2 rounded-lg transition-colors ${
                !isCurrentMonth ? 'opacity-30' : 'hover:bg-bg-secondary'
              }`}
            >
              <span className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full ${
                isCurrentDay
                  ? 'bg-teal text-white'
                  : isCurrentMonth ? 'text-text-primary' : 'text-text-muted'
              }`}>
                {format(day, 'd')}
              </span>
              {/* Event dots */}
              {memberColors.length > 0 && (
                <div className="flex gap-0.5 mt-0.5">
                  {memberColors.map((color, i) => (
                    <div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
